import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import type {
  DealItemGenericProperty,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useDealNotEditableHandler } from '@/lib/hooks/useDealNotEditableHandler';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import { datasourceExcludedUpdatedPromptState } from '@/states/datasource-excluded-updated-prompt.state';
import { productsState } from '@/states/products.state';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAtom, useAtomValue } from 'jotai';
import { AlertTriangle } from 'lucide-react';

type DatasourceExcludedUpdatedDialogProps = {
  dealId: string;
};

const HTTP_URL_RE = /^https?:\/\//i;

function nonempty(s: string | null | undefined): string | null {
  const t = s?.trim();
  return t ? t : null;
}

/** Options visibles (hors pseudo-option _index). */
function formatOptionsLine(
  options: DealItemGenericProperty[] | null | undefined
): string | null {
  const filtered = (options ?? []).filter((o) => o.name !== '_index');
  if (filtered.length === 0) return null;
  return filtered.map((o) => `${o.name}: ${o.value}`).join(' · ');
}

/**
 * GTIN + SKU si GTIN renseigné ; sinon Shopify variant ID ; sinon SKU ;
 * si ni GTIN ni SKU ni Shopify → URL source.
 */
function formatIdentificationLine(v: {
  gtin?: string | null;
  sku?: string | null;
  shopifyVariantId?: string | null;
  sourceUrl?: string | null;
}): { text: string; isUrl: boolean } | null {
  const gtin = nonempty(v.gtin);
  const sku = nonempty(v.sku);
  const shopify = nonempty(v.shopifyVariantId);
  const url = nonempty(v.sourceUrl);

  if (gtin) {
    const parts = [`GTIN: ${gtin}`];
    if (sku) parts.push(`SKU: ${sku}`);
    return { text: parts.join(' · '), isUrl: false };
  }
  if (shopify) {
    return { text: `Shopify: ${shopify}`, isUrl: false };
  }
  if (sku) {
    return { text: `SKU: ${sku}`, isUrl: false };
  }
  if (url) {
    return { text: url, isUrl: true };
  }
  return null;
}

type ExcludedUpdatedRow = {
  id: string;
  name: string;
  optionsLine: string | null;
  identification: { text: string; isUrl: boolean } | null;
};

const ExcludedUpdatedRowCard = memo(function ExcludedUpdatedRowCard({
  row,
}: {
  row: ExcludedUpdatedRow;
}) {
  const id = row.identification;
  const isLinkableUrl = Boolean(id?.isUrl && HTTP_URL_RE.test(id.text));

  return (
    <div className="flex flex-col gap-0.5 rounded-md border bg-muted/50 px-3 py-2">
      <span className="truncate text-sm font-medium">{row.name}</span>
      {row.optionsLine ? (
        <span className="break-words text-xs text-muted-foreground">
          {row.optionsLine}
        </span>
      ) : null}
      {id ? (
        isLinkableUrl ? (
          <a
            href={id.text}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-xs text-primary underline"
          >
            {id.text}
          </a>
        ) : (
          <span className="break-all text-xs text-muted-foreground">
            {id.text}
          </span>
        )
      ) : null}
    </div>
  );
});

/** Au-delà de ce seuil on virtualise ; en dessous, liste simple = premier rendu immédiat. */
const VIRTUALIZE_THRESHOLD = 80;

/** Overscan initial bas = peu de nœuds à mesurer au 1er paint (modale instantanée). */
const LIST_OVERSCAN_INITIAL = 8;
/** Overscan cible au scroll pour fluidité. */
const LIST_OVERSCAN_SCROLL = 50;

export function DatasourceExcludedUpdatedDialog({
  dealId,
}: DatasourceExcludedUpdatedDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [prompt, setPrompt] = useAtom(datasourceExcludedUpdatedPromptState);
  const products = useAtomValue(productsState);

  useEffect(() => {
    if (prompt && prompt.dealId !== dealId) {
      setPrompt(null);
    }
  }, [dealId, prompt, setPrompt]);
  const { updateProducts, loading } = useUpdateDealItems(dealId);
  const { handleIfDealNotEditable } = useDealNotEditableHandler();
  const [submitting, setSubmitting] = useState(false);

  const open =
    prompt != null && prompt.dealId === dealId && prompt.dealItemIds.length > 0;

  const productById = useMemo(() => {
    const m = new Map<string, DealItemWithVersions>();
    for (const p of products) {
      m.set(p.id, p);
    }
    return m;
  }, [products]);

  const rows = useMemo(() => {
    if (!prompt || prompt.dealId !== dealId) return [];
    return prompt.dealItemIds.map((id) => {
      const p = productById.get(id);
      const av = p?.activeVersion;
      const optionsLine = av
        ? formatOptionsLine(av.options ?? undefined)
        : null;
      const identification = av
        ? formatIdentificationLine({
            gtin: av.gtin,
            sku: av.sku,
            shopifyVariantId: av.shopifyVariantId,
            sourceUrl: av.sourceUrl,
          })
        : null;
      return {
        id,
        name: av?.name ?? '—',
        optionsLine,
        identification,
      };
    });
  }, [prompt, dealId, productById]);

  const listScrollParentRef = useRef<HTMLDivElement>(null);
  const shouldVirtualize = rows.length > VIRTUALIZE_THRESHOLD;

  const [listOverscan, setListOverscan] = useState(LIST_OVERSCAN_INITIAL);

  const rowVirtualizer = useVirtualizer({
    count: shouldVirtualize ? rows.length : 0,
    getScrollElement: () => listScrollParentRef.current,
    estimateSize: () => 96,
    overscan: listOverscan,
    getItemKey: (index) => rows[index]?.id ?? index,
    // Dialog Radix anime le content (transform) : mesurer sur rAF évite un premier range vide / retard.
    useAnimationFrameWithResizeObserver: true,
  });

  const virtualizerRef = useRef(rowVirtualizer);
  virtualizerRef.current = rowVirtualizer;

  useLayoutEffect(() => {
    if (!open || !shouldVirtualize || rows.length === 0) {
      setListOverscan(LIST_OVERSCAN_INITIAL);
      return;
    }

    setListOverscan(LIST_OVERSCAN_INITIAL);
    virtualizerRef.current.measure();

    let proceed = true;
    requestAnimationFrame(() => {
      if (!proceed) return;
      requestAnimationFrame(() => {
        if (!proceed) return;
        setListOverscan(LIST_OVERSCAN_SCROLL);
      });
    });

    return () => {
      proceed = false;
    };
  }, [open, shouldVirtualize, rows.length]);

  const busy = loading || submitting;

  const handleOpenChange = (next: boolean) => {
    if (busy) return;
    if (!next) {
      setPrompt(null);
    }
  };

  const handleKeepExcluded = () => {
    setPrompt(null);
  };

  const handleIncludeInOffer = async () => {
    if (!prompt || prompt.dealId !== dealId) return;
    setSubmitting(true);
    try {
      const items = prompt.dealItemIds.map((id) => ({ id, isSelected: true }));
      const result = await updateProducts(items);
      const response = result.data?.updateDealItemsV2;
      if (response?.__typename === 'DomainException') {
        if (handleIfDealNotEditable(response)) {
          setPrompt(null);
          return;
        }
        toast({
          title: t('general.error'),
          description: t('general.error_cannot_save_modifications'),
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: t('general.success'),
        description: t(
          'components.deal_items_subscription.excluded_updated_prompt_success'
        ),
      });
      setPrompt(null);
    } catch (e) {
      console.error('include excluded after datasource:', e);
      toast({
        title: t('general.error'),
        description: t('general.error_network_message'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            {t(
              'components.deal_items_subscription.excluded_updated_prompt_title'
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              'components.deal_items_subscription.excluded_updated_prompt_description',
              { count: rows.length }
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={listScrollParentRef}
          className="max-h-[250px] overflow-auto pr-3 [scrollbar-gutter:stable]"
        >
          {shouldVirtualize ? (
            <div
              className="relative w-full"
              style={{ height: rowVirtualizer.getTotalSize() }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index]!;
                return (
                  <div
                    key={row.id}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    className="absolute left-0 top-0 w-full pb-2"
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <ExcludedUpdatedRowCard row={row} />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col gap-2 pr-0.5">
              {rows.map((row) => (
                <ExcludedUpdatedRowCard key={row.id} row={row} />
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleKeepExcluded}
            disabled={busy}
          >
            {t(
              'components.deal_items_subscription.excluded_updated_prompt_keep_excluded'
            )}
          </Button>
          <Button
            onClick={handleIncludeInOffer}
            disabled={busy}
            className="gap-2"
          >
            {busy ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : null}
            {t(
              'components.deal_items_subscription.excluded_updated_prompt_include'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
